<template>
  <v-card outlined class="elevation-0 ma-4">
      <div class="panel-header pa-4">
        <div class="mtm-header mb-2">
          <!-- Search and filter -->
          <div class="panel-tools">
            <v-text-field
              v-model="positionQuery"
              :placeholder="$t('portfolioPositions.searchPositions')"
              dense
              hide-details
              solo
              flat
              background-color="transparent"
              class="search-positions"
              prepend-inner-icon="mdi-magnify"
            />
            <v-btn v-if="false" small class="btn-outline" depressed>{{ $t('instructions.filter') }}</v-btn>
          </div>

          <!-- Controls -->
          <div class="mtm-controls">

            <!-- Quando PAUSADO, o botão fica verde (mesma cor do LIVE) -->
            <button
              class="btn-pause"
              :class="{ 'is-resume': !isLive || !mockedEnabled }"
              @click="isLive = !isLive"
            >
              {{ mockedEnabled && isLive  ? $t('portfolioPositions.pauseFeed') : $t('portfolioPositions.resumeFeed') }}
            </button>

            <!-- Quando PAUSADO, o chip fica amarelo (cor do botão) e sem animação -->
            <v-chip
              small
              class="pill-live"
              :class="{ 'is-paused': !isLive || !mockedEnabled }"
            >
              {{ mockedEnabled && isLive ? $t('portfolioPositions.live') : $t('portfolioPositions.paused') }}
            </v-chip
              >
          </div>
        </div>

        <!-- Métricas -->
        <div class="mtm-metrics">
          <div class="metric-card">
            <div class="metric-label">{{ $t('portfolioPositions.totalUnrealizedPnl') }}</div>
            <div class="metric-value" :class="totalUnrealizedPnl>=0?'up':'down'">
              {{ formatMoney(totalUnrealizedPnl, currencyCode, true, true) }}
            </div>
          </div>
          <div class="metric-card">
            <div class="metric-label">{{ $t('portfolioPositions.portfolioValue') }}</div>
            <div class="metric-value">
              {{ formatMoney(portfolioValue, currencyCode, false, true) }}
            </div>
          </div>
          <div class="metric-card">
            <div class="metric-label">{{ $t('portfolioPositions.dayChange') }}</div>
            <div class="metric-value" :class="dayChange>=0?'up':'down'">
              {{ formatMoney(dayChange, currencyCode, true, true) }}
            </div>
          </div>
          <div class="metric-card">
            <div class="metric-label">{{ $t('portfolioPositions.marketStatus') }}</div>
            <div class="metric-value up">{{ $t('portfolioPositions.open') }}</div>
          </div>
        </div>
      </div>

      <!-- Tabela -->
      <div class="table-wrap">
        <base-data-table
          :headers="positionsHeaders"
          :items="filteredPositions"
          :items-per-page="-1"
          :mobile-breakpoint="0"
          hide-default-footer
          dense
          class="table elevation-0 rt-table"
        >
          <!-- Symbol + time -->
          <template slot="item.symbol" slot-scope="{ item }">
            <div class="symbol">{{ item.symbol }}</div>
            <div class="time">{{ item.time }}</div>
          </template>

          <!-- Last Price -->
          <template slot="item.last" slot-scope="{ item }">
            <div class="mono strong">USD&nbsp;&nbsp;{{ item.last.toFixed(2) }}</div>
          </template>

          <!-- Bid/Ask -->
          <template slot="item.bidask" slot-scope="{ item }">
            <div class="hint">{{ $t('portfolioPositions.bidPrefix') }} USD {{ item.bid.toFixed(2) }}</div>
            <div class="hint">{{ $t('portfolioPositions.askPrefix') }} USD {{ item.ask.toFixed(2) }}</div>
          </template>

          <!-- Change -->
          <template slot="item.change" slot-scope="{ item }">
            <div :class="item.changePct >= 0 ? 'up' : 'down'">
              {{ item.changePct >= 0 ? '+' : '' }}{{ (item.changePct * 100).toFixed(2) }}%
            </div>
            <div class="hint">
              {{ item.changeAbs >= 0 ? '+' : '' }}USD {{ Math.abs(item.changeAbs).toFixed(2) }}
            </div>
          </template>

          <!-- Quantity -->
          <template slot="item.quantity" slot-scope="{ item }">
            <div class="td-right">{{ formatNumber(item.quantity) }}</div>
          </template>

          <!-- Weight -->
          <template slot="item.weight" slot-scope="{ item }">
            <div class="td-right">{{ item.weight }}</div>
          </template>

          <!-- Avg Cost -->
          <template slot="item.avgCost" slot-scope="{ item }">
            <div class="td-right">USD {{ item.avgCost.toFixed(2) }}</div>
          </template>

          <!-- Market Value -->
          <template slot="item.marketValue" slot-scope="{ item }">
            <div class="td-right mono">USD&nbsp;{{ formatNumber(item.marketValue) }}</div>
          </template>

          <!-- Unrealized PnL -->
          <template slot="item.pnl" slot-scope="{ item }">
            <div :class="['strong', item.pnl >= 0 ? 'up' : 'down']">
              {{ item.pnl >= 0 ? '+USD' : 'USD' }}&nbsp;{{ formatNumber(Math.abs(item.pnl)) }}
            </div>
            <div class="hint">{{ $t('portfolioPositions.bidDiff') }} {{ item.bidDiff >= 0 ? '+USD' : 'USD' }} {{ formatNumber(Math.abs(item.bidDiff)) }}</div>
            <div class="hint">{{ $t('portfolioPositions.askDiff') }} {{ item.askDiff >= 0 ? '+USD' : 'USD' }} {{ formatNumber(Math.abs(item.askDiff)) }}</div>
          </template>
        </base-data-table>
      </div>

      <!-- Totalizadores -->
      <div class="mtm-summaries">
        <section class="summary-card">
          <header class="summary-header">
            <span class="bullet up"></span>
            <h3>{{ $t('portfolioPositions.topPerformer') }}</h3>
          </header>
          <div class="summary-body">
            <div class="symbol">{{ topPerformer?.symbol || "-" }}</div>
            <div class="value" :class="(topPerformer?.dayChangeAbs||0)>=0?'up':'down'">
              {{ formatMoney(topPerformer?.dayChangeAbs||0, currencyCode, true) }}
            </div>
          </div>
        </section>

        <section class="summary-card">
          <header class="summary-header">
            <span class="bullet down"></span>
            <h3>{{ $t('portfolioPositions.biggestDecline') }}</h3>
          </header>
          <div class="summary-body">
            <div class="symbol">{{ biggestDecline?.symbol || "-" }}</div>
            <div class="value" :class="(biggestDecline?.dayChangeAbs||0)>=0?'up':'down'">
              {{ formatMoney(biggestDecline?.dayChangeAbs||0, currencyCode, true) }}
            </div>
          </div>
        </section>
      </div>

  </v-card>
</template>

<script>
import { mapStores } from 'pinia'
import { usePortfolioStore } from '@/store/portfolioStore.js'

export default {
  name: 'PortfolioPositions',
  props:{mockedEnabled:{type:Boolean}},
  data () {
    return {
      positionQuery: '',

      /* ===== Feed mock ===== */
     // mockFeedEnabled: true, // toggle geral
      isLive: true,          // pause/play
      livePrices: {},        // preços atuais visíveis na UI
      liveCache: {},         // { [portfolioId]: { [symbol]: LivePrice } }
      _feedTimer: null
    }
  },

  computed: {
    ...mapStores(usePortfolioStore),

    positionsHeaders () {
      return [
        { text: this.$t('pricer.headers.symbol'), value: 'symbol',      sortable: false },
        { text: this.$t('portfolioPositions.headers.lastPrice'), value: 'last',        sortable: false, align: 'end' },
        { text: this.$t('portfolioPositions.headers.bidAsk'),    value: 'bidask',      sortable: false, align: 'end' },
        { text: this.$t('marketMonitor.priceVariation'), value: 'change',      sortable: false, align: 'end' },
        { text: this.$t('portfolioPositions.headers.qty'), value: 'quantity',    sortable: false, align: 'end' },
        { text: this.$t('portfolioPositions.headers.weight'), value: 'weight',      sortable: false, align: 'end' },
        { text: this.$t('portfolioPositions.headers.avgCost'), value: 'avgCost',     sortable: false, align: 'end' },
        { text: this.$t('portfolioPositions.headers.marketValue'), value: 'marketValue', sortable: false, align: 'end' },
        { text: this.$t('portfolioPositions.headers.unrealizedPnl'), value: 'pnl',         sortable: false, align: 'end' }
      ]
    },

    positions () {
      return this.portfolioStore.getPositions || []
    },
    selectedPortfolio () {
      return this.portfolioStore.selectedPortfolio
    },
    selectedPortfolioId () {
      return this.portfolioStore.selectedPortfolioId
    },
    // ViewModel: mantém CHANGE/PNL baseados em avgCost; se houver feed, injeta last/bid/ask/time do feed
    positionsVm () {
      const num = v => (v == null || Number.isNaN(Number(v))) ? 0 : Number(v)

      const base = (this.positions || []).map(p => {
        const quantity = num(p.quantity)
        const avgCost  = num(p.avgCost)

        // Defaults sem feed
        let last = p.last != null ? num(p.last) : avgCost
        let bid  = p.bid  != null ? num(p.bid)  : last * 0.9995
        let ask  = p.ask  != null ? num(p.ask)  : last * 1.0005
        let time = p.time || new Date().toLocaleTimeString()

        // Valores ao vivo, se existirem
        const live = this.livePrices[p.symbol]
        if (live) {
          last = num(live.lastPrice)
          bid  = num(live.bid)
          ask  = num(live.ask)
          time = new Date(live.timestamp).toLocaleTimeString()
        }

        const marketValue = quantity * last
        const pnl         = (last - avgCost) * quantity
        const changeAbs   = (last - avgCost)
        const changePct   = avgCost ? (last / avgCost - 1) : 0
        const bidDiff     = (bid - avgCost) * quantity
        const askDiff     = (ask - avgCost) * quantity

        return {
          ...p,
          quantity, avgCost,
          last, bid, ask,
          marketValue, pnl,
          changeAbs, changePct,
          bidDiff, askDiff,
          time,
          weight: p.weight || '0.00%'
        }
      })

      return base
    },

    // Filtro
    filteredPositions () {
      const q = (this.positionQuery || '').trim().toLowerCase()
      if (!q) return this.positionsVm
      return this.positionsVm.filter(r =>
        (r.symbol && r.symbol.toLowerCase().includes(q)) ||
        (r.name && r.name.toLowerCase().includes(q))
      )
    },

    /* Totalizadores usando feed (mesma regra do React) */
    currencyCode () {
      return (this.selectedPortfolio && this.selectedPortfolio.currency) || 'USD'
    },
    portfolioValue () {
      return (this.positions || []).reduce((sum, p) => {
        const lp = this.livePrices[p.symbol]
        const qty = Number(p.quantity) || 0
        const last = lp ? Number(lp.lastPrice) || 0 : (Number(p.last) || Number(p.avgCost) || 0)
        return sum + (qty * last)
      }, 0)
    },
    totalUnrealizedPnl () {
      return (this.positions || []).reduce((sum, p) => {
        const lp = this.livePrices[p.symbol]
        const qty = Number(p.quantity) || 0
        const avg = Number(p.avgCost) || 0
        const last = lp ? Number(lp.lastPrice) || 0 : (Number(p.last) || avg)
        return sum + ((last - avg) * qty)
      }, 0)
    },
    dayChange () {
      return (this.positions || []).reduce((sum, p) => {
        const lp = this.livePrices[p.symbol]
        const qty = Number(p.quantity) || 0
        const chg = lp ? Number(lp.change) || 0 : 0
        return sum + (chg * qty)
      }, 0)
    },
    topPerformer () {
      let best = null, bestVal = -Infinity
      const rows = this.positions || []
      for (let i = 0; i < rows.length; i++) {
        const p = rows[i]
        const lp = this.livePrices[p.symbol]
        if (!lp) continue
        const qty = Number(p.quantity) || 0
        const avg = Number(p.avgCost) || 0
        const pnl = (Number(lp.lastPrice) - avg) * qty
        if (pnl > bestVal) { bestVal = pnl; best = { symbol: p.symbol, dayChangeAbs: pnl } }
      }
      return best
    },
    biggestDecline () {
      let worst = null, worstVal = Infinity
      const rows = this.positions || []
      for (let i = 0; i < rows.length; i++) {
        const p = rows[i]
        const lp = this.livePrices[p.symbol]
        if (!lp) continue
        const qty = Number(p.quantity) || 0
        const avg = Number(p.avgCost) || 0
        const pnl = (Number(lp.lastPrice) - avg) * qty
        if (pnl < worstVal) { worstVal = pnl; worst = { symbol: p.symbol, dayChangeAbs: pnl } }
      }
      return worst
    }
  },

  watch: {
    // Troca de portfólio: salva o anterior e recarrega o novo
    selectedPortfolioId: {
      handler (newId, oldId) {
        if (oldId) this._saveLivePrices(oldId)
        this._loadOrInit(newId)
        // Ajusta o timer conforme estado atual
        if (this.mockedEnabled && this.isLive) this._startFeed()
        else this._stopFeed()
      },
      immediate: true
    },
    isLive (on) {
      if (on && this.mockedEnabled) this._startFeed()
      else this._stopFeed()
    },
  },

  beforeDestroy () {
    // salva estado atual ao sair do componente
    if (this.selectedPortfolioId) this._saveLivePrices(this.selectedPortfolioId)
    this._stopFeed()
  },

  methods: {
    /* ---------- Cache por portfólio ---------- */
    _saveLivePrices (portfolioId) {
      if (!portfolioId) return
      // clone raso suficiente (os valores são números/strings)
      this.liveCache[portfolioId] = JSON.parse(JSON.stringify(this.livePrices || {}))
    },
    _loadOrInit (portfolioId) {
      if (!portfolioId) { this.livePrices = {}; return }
      const cached = this.liveCache[portfolioId]
      if (cached && Object.keys(cached).length) {
        // recarrega último snapshot (para “voltar” e ver os mesmos números)
        this.livePrices = JSON.parse(JSON.stringify(cached))
      } else {
        // primeira vez nesse portfólio: cria preços iniciais a partir das posições
        this._initLivePrices(this.positions || [])
      }
    },

    /* ---------- Inicialização de preços ---------- */
    _initLivePrices (rows) {
      const book = {}
      for (let i = 0; i < rows.length; i++) {
        const r = rows[i]
        const sym = r.symbol
        if (!sym) continue
        const last = (Number(r.last) || Number(r.avgCost) || 0)
        const spread = Math.max(0.0001, last * 0.001) // ~0.1%
        book[sym] = {
          symbol: sym,
          lastPrice: last,
          bid: Math.max(0.01, last - spread / 2),
          ask: last + spread / 2,
          change: 0,
          changePercent: 0,
          timestamp: Date.now()
        }
      }
      this.livePrices = book
      // também salva no cache do portfólio atual
      if (this.selectedPortfolioId) this._saveLivePrices(this.selectedPortfolioId)
    },

    /* ---------- Timer do feed ---------- */
    _startFeed () {
      this._stopFeed()
      const VOL = 0.002 // 0.2%
      this._feedTimer = setInterval(() => {
        const next = { ...this.livePrices }
        for (const sym in next) {
          const cur = next[sym]
          const rand = (Math.random() - 0.5) * 2 * VOL
          const newPx = Math.max(0.01, cur.lastPrice * (1 + rand))
          const spread = Math.max(0.0001, newPx * 0.001)
          const chg = newPx - cur.lastPrice
          const chgPct = cur.lastPrice ? (chg / cur.lastPrice) * 100 : 0
          next[sym] = {
            ...cur,
            lastPrice: newPx,
            bid: Math.max(0.01, newPx - spread / 2),
            ask: newPx + spread / 2,
            change: chg,
            changePercent: chgPct,
            timestamp: Date.now()
          }
        }
        this.livePrices = next
        // persiste o snapshot do portfólio atual
        if (this.selectedPortfolioId) this._saveLivePrices(this.selectedPortfolioId)
      }, 5000)
    },
    _stopFeed () {
      if (this._feedTimer) {
        clearInterval(this._feedTimer)
        this._feedTimer = null
      }
    },

    /* ---------- Utilitários ---------- */
    formatNumber (n) {
      try {
        return new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(n)
      } catch (e) { return n }
    },
    formatMoney (value, ccy = 'USD', withSign = true, compact = false) {
      const n = Number(value) || 0
      const sign = withSign ? (n > 0 ? '+' : n < 0 ? '-' : '') : ''
      const abs = Math.abs(n)
      if (compact) {
        if (abs >= 1e9) return sign + ccy + ' ' + (abs/1e9).toFixed(2) + 'B'
        if (abs >= 1e6) return sign + ccy + ' ' + (abs/1e6).toFixed(2) + 'M'
        if (abs >= 1e3) return sign + ccy + ' ' + (abs/1e3).toFixed(2) + 'K'
      }
      let num = ''
      try {
        num = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(abs)
      } catch (e) { num = abs.toFixed(2) }
      return sign + ccy + ' ' + num
    }
  }
}
</script>

<style>

/* ---------- Header ---------- */
.mtm-header{display:flex;justify-content:space-between;align-items:center;margin:8px;}
.mtm-title h2{font-size:20px;font-weight:700;color:var(--text);margin:0;}
.mtm-title p{color:var(--muted);font-size:14px;margin:2px 0 0;}
.mtm-controls{display:flex;align-items:center;gap:12px;}

/* Switch */
.mock-toggle{position:relative;display:inline-flex;align-items:center;font-size:14px;color:var(--text);gap:6px;}
.mock-toggle input{opacity:0;width:0;height:0;}
.mock-toggle .slider{width:34px;height:18px;background-color:var(--accent);border-radius:16px;position:relative;cursor:pointer;}
.mock-toggle .slider::before{content:"";position:absolute;width:14px;height:14px;left:2px;top:2px;background:#fff;border-radius:50%;transition:transform .2s;}
.mock-toggle input:checked + .slider::before{transform:translateX(16px);}

/* Buttons / Pills */
/* Botão padrão (ON): amarelo */
.btn-pause{
  background: var(--main-color);
  border: none;
  padding: 6px 14px;
  border-radius: 6px;
  font-size: 14px;
  font-weight:700;
  cursor:pointer;
  color:#0b0b0b;
}

/* Quando PAUSADO: botão "Resume" fica VERDE (igual ao chip LIVE) */
.btn-pause.is-resume{
  background: var(--success);
  color: #0b0b0b; /* bom contraste no verde */
}

/* Chip padrão (ON): verde com animação */
.pill-live {
  font-weight: 700;
  background: rgba(45, 197, 22, 0.179) !important;
  border: 0.06rem solid #00ff2a !important;
  color:#00ff2a !important;
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

/* Quando PAUSADO: chip fica AMARELO e sem animação */
.pill-live.is-paused {
  background: rgba(197, 107, 22, 0.235) !important;
  border: 0.06rem solid var(--accent) !important;
  color: var(--accent) !important;
  animation: none;
}

@keyframes pulse {
  50% {
    opacity: 0.5;
  }
}

/* ---------- Métricas ---------- */
.mtm-metrics{display:grid;grid-template-columns:repeat(4,1fr);gap:16px;margin:16px 8px;}
.metric-card{background:var(--surface);border:1px solid var(--border);border-radius:8px;padding:16px;}
.metric-label{color:var(--muted);font-size:13px;margin-bottom:6px;}
.metric-value{color:var(--text);font-weight:800;font-size:20px;}

/* ---------- Panel ---------- */
.panel {

  border-radius:4px;
}

.panel-header{display:flex;flex-direction:column;justify-content:space-between;}
.panel-tools{display:flex;gap:12px;align-items:center;}
.search-positions{width:340px;background:#111318 !important;border:1px solid var(--border);border-radius:8px;}
.btn-outline{background:#101216 !important;color:var(--text) !important;border:1px solid var(--border) !important;text-transform:none;}

/* ---------- Tabela ---------- */
.table-wrap{overflow-x:auto;}
.table{width:100%;border-collapse:collapse;}
/* Cabeçalho */
.table thead th{
  font-size:11px;letter-spacing:.06em;color:#aeb4bf;text-align:left;
  border-bottom:1px solid var(--border);padding:12px 16px;font-weight:700;
}
/* Linhas */
.table tbody td{
  border-bottom:1px solid #232730;padding:14px 16px;font-size:14px;color:var(--text);
}
.table tbody tr:hover{background:#171a20;}

.rt-table.v-data-table{background:transparent;border-radius:0;}
.rt-table .v-data-table__wrapper table tbody tr td{
  border-bottom:1px solid #232730 !important;padding:16px 16px !important;vertical-align:top;color:var(--text);
}
.rt-table .v-data-table__wrapper tbody tr:hover{background:#171a20;}
.rt-table .v-data-table__progress,.rt-table .v-data-table__bottom{display:none;}

/* ---------- Tipografia / Cores utilitárias ---------- */
.mono{font-family:ui-monospace,SFMono-Regular,Menlo,Monaco,Consolas,"Liberation Mono","Courier New",monospace;}
.strong{font-weight:800;}
.hint{font-size:12px;color:var(--muted);margin-top:2px;}
.symbol{font-weight:800;color:var(--text);}
.time{font-size:12px;color:var(--muted);margin-top:2px;}
.up{color:var(--success);font-weight:800;}
.down{color:var(--danger);font-weight:800;}
.td-right{text-align:right;color:#cdd1d8;}

/* ---------- Summaries ---------- */
.mtm-summaries{display:grid;grid-template-columns:1fr 1fr;gap:16px;margin:16px 8px 8px;}
.summary-card{background:var(--surface);border:1px solid var(--border);border-radius:10px;padding:16px 18px;}
.summary-header{display:flex;align-items:center;gap:8px;margin-bottom:14px;}
.summary-header h3{margin:0;font-size:18px;font-weight:800;color:var(--text);}
.bullet{width:10px;height:10px;border-radius:50%;display:inline-block;}
.bullet.up{background:var(--success);}
.bullet.down{background:var(--danger);}
.summary-body .symbol{color:var(--text);font-weight:800;letter-spacing:.02em;margin-bottom:8px;}
.value{font-size:22px;font-weight:900;letter-spacing:.02em;}
/* .value.up / .value.down herdadas de .up/.down */
</style>